import React, { useEffect, useState, useRef } from "react";
import { getUser, updateProfile, getMyOrders, uploadProfilePicture, deleteProfilePicture } from "../common/api/api";
import { useNavigate } from "react-router-dom";

const STATUS_STEPS = ["pending", "processing", "shipped", "delivered"];

const STATUS_COLORS = {
  pending:    "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  processing: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  shipped:    "bg-purple-500/20 text-purple-400 border-purple-500/30",
  delivered:  "bg-green-500/20 text-green-400 border-green-500/30",
  cancelled:  "bg-red-500/20 text-red-400 border-red-500/30",
};

const PAYMENT_COLORS = {
  pending: "text-yellow-400",
  paid:    "text-green-400",
  failed:  "text-red-400",
  refunded:"text-blue-400",
};

export default function UserProfile() {
  const navigate = useNavigate();
  const fileRef = useRef();

  const [tab, setTab] = useState("profile"); // 'profile' | 'orders'
  const [user, setUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [picUploading, setPicUploading] = useState(false);

  // editable fields
  const [form, setForm] = useState({ name: "", mobile: "", address: "", city: "", postalCode: "" });

  useEffect(() => {
    if (!localStorage.getItem("auth-token")) {
      navigate("/login");
      return;
    }
    let mounted = true;

    const loadProfile = async () => {
      setLoading(true);
      try {
        const [userRes, ordersRes] = await Promise.allSettled([getUser(), getMyOrders()]);

        if (!mounted) return;

        if (userRes.status !== "fulfilled" || !userRes.value || userRes.value.errors || userRes.value.error) {
          localStorage.removeItem("auth-token");
          navigate("/login");
          return;
        }

        const userData = userRes.value;
        setUser(userData);
        setForm({
          name: userData.name || "",
          mobile: userData.mobile || "",
          address: userData.address || "",
          city: userData.city || "",
          postalCode: userData.postalCode || "",
        });

        if (ordersRes.status === "fulfilled" && Array.isArray(ordersRes.value)) {
          setOrders(ordersRes.value);
        } else {
          setOrders([]);
        }
      } catch (error) {
        if (mounted) {
          setOrders([]);
          navigate("/login");
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadProfile();

    return () => {
      mounted = false;
    };
  }, [navigate]);

  const handleChange = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaveMsg("");
    try {
      const res = await updateProfile(form);
      if (res.success) {
        setUser(res.user);
        localStorage.setItem("user-name", res.user.name);
        setSaveMsg("Profile updated successfully!");
      } else {
        setSaveMsg(res.error || "Failed to update profile.");
      }
    } catch (error) {
      setSaveMsg("Failed to update profile.");
    } finally {
      setSaving(false);
    }
    setTimeout(() => setSaveMsg(""), 3000);
  };

  const handlePicChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPicUploading(true);
    const fd = new FormData();
    fd.append("profile", file);
    try {
      const res = await uploadProfilePicture(fd);
      if (res.success) {
        // delete old picture from cloudinary if exists
        if (user.profilePicturePublicId) {
          await deleteProfilePicture(user.profilePicturePublicId);
        }
        const upRes = await updateProfile({ profilePicture: res.url, profilePicturePublicId: res.publicId });
        if (upRes.success) {
          setUser(upRes.user);
          localStorage.setItem("user-profile-picture", res.url);
        }
      }
    } catch (error) {
      // Keep UI responsive even if upload/update fails
    } finally {
      setPicUploading(false);
    }
  };

  const stepIndex = (status) => STATUS_STEPS.indexOf(status);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#060c18" }}>
        <div className="w-10 h-10 border-2 border-[#00d4ff] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-10 px-4" style={{ background: "#060c18" }}>
      <div className="max-w-4xl mx-auto">

        {/* Header card */}
        <div className="rounded-2xl p-6 mb-6 flex items-center gap-5 flex-wrap" style={{ background: "rgba(8,16,32,0.95)", border: "1px solid rgba(0,212,255,0.15)" }}>
          {/* Avatar */}
          <div className="relative flex-shrink-0">
            <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-[rgba(0,212,255,0.4)] bg-[#0d1a2e] flex items-center justify-center text-4xl cursor-pointer group" onClick={() => fileRef.current.click()}>
              {user?.profilePicture ? (
                <img src={user.profilePicture} alt="avatar" className="w-full h-full object-cover" />
              ) : (
                <span>👤</span>
              )}
              <div className="absolute inset-0 bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <span className="text-white text-xs font-semibold">{picUploading ? "..." : "Edit"}</span>
              </div>
            </div>
            <input type="file" accept="image/*" ref={fileRef} className="hidden" onChange={handlePicChange} />
          </div>

          <div className="flex-1 min-w-0">
            <h1 className="text-white text-2xl font-bold truncate">{user?.name || "User"}</h1>
            <p className="text-[#a0aec0] text-sm mt-0.5">{user?.email}</p>
            <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-semibold border" style={{ background: "rgba(0,212,255,0.1)", color: "#00d4ff", borderColor: "rgba(0,212,255,0.3)" }}>
              {user?.role || "user"}
            </span>
          </div>

          <div className="text-right">
            <p className="text-[#a0aec0] text-xs">Member since</p>
            <p className="text-white font-semibold text-sm">{user?.date ? new Date(user.date).toLocaleDateString("en-PK", { year: "numeric", month: "long" }) : "—"}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {["profile", "orders"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-all border capitalize ${tab === t ? "bg-[rgba(0,212,255,0.15)] text-[#00d4ff] border-[rgba(0,212,255,0.4)]" : "bg-transparent text-[#a0aec0] border-[rgba(255,255,255,0.06)] hover:text-white hover:border-[rgba(0,212,255,0.2)]"}`}
            >
              {t === "orders" ? `My Orders (${orders.length})` : "Profile Details"}
            </button>
          ))}
        </div>

        {/* ── Profile Tab ─────────────────────────────────────────────────── */}
        {tab === "profile" && (
          <div className="rounded-2xl p-6" style={{ background: "rgba(8,16,32,0.95)", border: "1px solid rgba(0,212,255,0.12)" }}>
            <h2 className="text-white text-lg font-bold mb-5">Edit Profile</h2>
            <form onSubmit={handleSave} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { label: "Full Name",    name: "name",       type: "text",  placeholder: "Your name" },
                { label: "Mobile",       name: "mobile",     type: "tel",   placeholder: "+92 3xx xxxxxxx" },
                { label: "City",         name: "city",       type: "text",  placeholder: "City" },
                { label: "Postal Code",  name: "postalCode", type: "text",  placeholder: "Postal code" },
              ].map(({ label, name, type, placeholder }) => (
                <div key={name}>
                  <label className="block text-[#a0aec0] text-xs font-semibold mb-1.5 uppercase tracking-wider">{label}</label>
                  <input
                    type={type}
                    name={name}
                    value={form[name]}
                    onChange={handleChange}
                    placeholder={placeholder}
                    className="w-full px-4 py-2.5 rounded-xl text-sm text-white placeholder-[#4a5568] outline-none transition-all focus:border-[rgba(0,212,255,0.5)] focus:shadow-[0_0_0_3px_rgba(0,212,255,0.08)]"
                    style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}
                  />
                </div>
              ))}

              {/* Address — full width */}
              <div className="md:col-span-2">
                <label className="block text-[#a0aec0] text-xs font-semibold mb-1.5 uppercase tracking-wider">Address</label>
                <textarea
                  name="address"
                  value={form.address}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Street, area..."
                  className="w-full px-4 py-2.5 rounded-xl text-sm text-white placeholder-[#4a5568] outline-none transition-all resize-none focus:border-[rgba(0,212,255,0.5)]"
                  style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}
                />
              </div>

              {/* Email (read-only) */}
              <div className="md:col-span-2">
                <label className="block text-[#a0aec0] text-xs font-semibold mb-1.5 uppercase tracking-wider">Email (read-only)</label>
                <input
                  type="email"
                  value={user?.email || ""}
                  readOnly
                  className="w-full px-4 py-2.5 rounded-xl text-sm text-[#a0aec0] outline-none"
                  style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", cursor: "not-allowed" }}
                />
              </div>

              <div className="md:col-span-2 flex items-center gap-4 mt-2">
                <button
                  type="submit"
                  disabled={saving}
                  className="px-6 py-2.5 rounded-xl font-semibold text-sm text-[#060c18] transition-all disabled:opacity-60"
                  style={{ background: "linear-gradient(135deg, #00d4ff, #7c3aed)" }}
                >
                  {saving ? "Saving..." : "Save Changes"}
                </button>
                {saveMsg && (
                  <p className={`text-sm font-medium ${saveMsg.includes("success") ? "text-green-400" : "text-red-400"}`}>{saveMsg}</p>
                )}
              </div>
            </form>
          </div>
        )}

        {/* ── Orders Tab ──────────────────────────────────────────────────── */}
        {tab === "orders" && (
          <div className="space-y-4">
            {orders.length === 0 ? (
              <div className="rounded-2xl p-12 text-center" style={{ background: "rgba(8,16,32,0.95)", border: "1px solid rgba(0,212,255,0.12)" }}>
                <p className="text-4xl mb-3">📦</p>
                <p className="text-white font-semibold text-lg">No orders yet</p>
                <p className="text-[#a0aec0] text-sm mt-1">Your order history will appear here.</p>
              </div>
            ) : (
              orders.map((order) => {
                const isExpanded = expandedOrder === order._id;
                const isCancelled = order.status === "cancelled";
                const trackStepIdx = isCancelled ? -1 : stepIndex(order.status);

                return (
                  <div key={order._id} className="rounded-2xl overflow-hidden" style={{ background: "rgba(8,16,32,0.95)", border: "1px solid rgba(0,212,255,0.12)" }}>
                    {/* Order header */}
                    <button
                      className="w-full text-left px-5 py-4 flex flex-wrap items-center gap-4 hover:bg-white/[0.02] transition-colors"
                      onClick={() => setExpandedOrder(isExpanded ? null : order._id)}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-white font-bold text-sm">{order.orderId}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${STATUS_COLORS[order.status] || STATUS_COLORS.pending}`}>
                            {order.status}
                          </span>
                        </div>
                        <p className="text-[#a0aec0] text-xs mt-1">{new Date(order.date).toLocaleDateString("en-PK", { day: "numeric", month: "short", year: "numeric" })}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-white font-bold">Rs. {order.totalAmount?.toLocaleString()}</p>
                        <p className={`text-xs font-medium ${PAYMENT_COLORS[order.paymentStatus] || "text-yellow-400"}`}>{order.paymentMethod?.toUpperCase()} · {order.paymentStatus}</p>
                      </div>
                      <span className="text-[#a0aec0] text-lg">{isExpanded ? "▲" : "▼"}</span>
                    </button>

                    {/* Expanded details */}
                    {isExpanded && (
                      <div className="border-t border-white/[0.05] px-5 py-4 space-y-5">

                        {/* Order Tracking */}
                        {!isCancelled ? (
                          <div>
                            <h4 className="text-[#a0aec0] text-xs font-semibold uppercase tracking-wider mb-3">Order Tracking</h4>
                            <div className="flex items-center gap-0">
                              {STATUS_STEPS.map((step, i) => {
                                const done    = i <= trackStepIdx;
                                const current = i === trackStepIdx;
                                const isLast  = i === STATUS_STEPS.length - 1;
                                return (
                                  <React.Fragment key={step}>
                                    <div className="flex flex-col items-center gap-1.5 min-w-[64px]">
                                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all border-2 ${done ? "bg-[#00d4ff] border-[#00d4ff] text-[#060c18]" : "bg-transparent border-white/20 text-white/30"} ${current ? "ring-2 ring-[#00d4ff]/40 ring-offset-2 ring-offset-[#060c18]" : ""}`}>
                                        {done ? "✓" : i + 1}
                                      </div>
                                      <span className={`text-[10px] font-semibold capitalize ${done ? "text-[#00d4ff]" : "text-white/30"}`}>{step}</span>
                                    </div>
                                    {!isLast && (
                                      <div className={`flex-1 h-0.5 mb-5 transition-all ${i < trackStepIdx ? "bg-[#00d4ff]" : "bg-white/10"}`} />
                                    )}
                                  </React.Fragment>
                                );
                              })}
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 py-2">
                            <span className="text-red-400 text-xl">✕</span>
                            <span className="text-red-400 font-semibold">This order has been cancelled.</span>
                          </div>
                        )}

                        {/* Products */}
                        <div>
                          <h4 className="text-[#a0aec0] text-xs font-semibold uppercase tracking-wider mb-3">Items Ordered</h4>
                          <div className="space-y-2">
                            {order.products?.map((p, idx) => (
                              <div key={idx} className="flex items-center gap-3 py-2 px-3 rounded-xl" style={{ background: "rgba(255,255,255,0.03)" }}>
                                {p.image && (
                                  <img src={p.image} alt={p.name} className="w-12 h-12 object-cover rounded-lg flex-shrink-0 bg-white/5" />
                                )}
                                <div className="flex-1 min-w-0">
                                  <p className="text-white text-sm font-medium truncate">{p.name}</p>
                                  <p className="text-[#a0aec0] text-xs">Qty: {p.quantity}</p>
                                </div>
                                <p className="text-white font-semibold text-sm flex-shrink-0">Rs. {(p.price * p.quantity)?.toLocaleString()}</p>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Summary row */}
                        <div className="flex flex-wrap gap-4 text-sm pt-2 border-t border-white/[0.06]">
                          {order.discount > 0 && (
                            <span className="text-green-400">Discount: -Rs. {order.discount?.toLocaleString()}</span>
                          )}
                          {order.couponCode && (
                            <span className="text-[#a0aec0]">Coupon: <span className="text-white font-semibold">{order.couponCode}</span></span>
                          )}
                          {order.address && (
                            <span className="text-[#a0aec0]">Delivery to: <span className="text-white">{order.address}</span></span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
}
